import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  LinearProgress,
  Fab,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  EmojiEvents,
  ExpandMore,
  Sort,
  FilterList,
  MoreVert,
  Visibility,
  VisibilityOff,
  ContentCopy,
  Archive,
  Unarchive,
  CheckBox,
  CheckBoxOutlineBlank,
  SelectAll,
  Clear,
  Upload,
  Download,
  Refresh
} from '@mui/icons-material';
import { adminAchievementService } from '../../../services/adminAchievementService';
import { useConfirm } from '../../../hooks/useConfirm';

const RARITY_OPTIONS = [
  { value: 'common', label: 'Common', color: '#9E9E9E' },
  { value: 'uncommon', label: 'Uncommon', color: '#4CAF50' },
  { value: 'rare', label: 'Rare', color: '#2196F3' },
  { value: 'epic', label: 'Epic', color: '#9C27B0' },
  { value: 'legendary', label: 'Legendary', color: '#FF9800' }
];

const CATEGORY_OPTIONS = [
  'SOCIAL', 'DISCOVERY', 'ENGAGEMENT', 'EXPERTISE', 'MILESTONE', 'STREAK', 'SPECIAL',
  'CONTENT', 'COLLECTION', 'GENRE', 'FRANCHISE'
];

const CONDITION_TYPES = [
  { value: 'FOLLOWER_COUNT', label: 'Follower Count', field: 'followersCount' },
  { value: 'REVIEW_COUNT', label: 'Review Count', field: 'reviewsCount' },
  { value: 'MOVIE_WATCHED_COUNT', label: 'Movies Watched Count', field: 'moviesWatched' },
  { value: 'LOGIN_STREAK', label: 'Login Streak', field: 'loginStreak' },
  { value: 'LIKES_RECEIVED', label: 'Likes Received', field: 'likesReceived' },
  { value: 'EARLY_DISCOVERIES', label: 'Early Discoveries', field: 'earlyDiscoveries' },
  { value: 'POSTS_COUNT', label: 'Posts Count', field: 'postsCount' },
  { value: 'ACTION_BASED', label: 'Action-Based (Repeatable Points)', field: null },
  { value: 'SPECIFIC_MOVIE_WATCHED', label: 'Specific Movie Watched', field: null },
  { value: 'SPECIFIC_SHOW_WATCHED', label: 'Specific Show Watched', field: null },
  { value: 'MOVIE_GROUP_WATCHED', label: 'Movie Group Watched', field: null },
  { value: 'GENRE_WATCHED_COUNT', label: 'Genre Watched Count', field: null },
  { value: 'FRANCHISE_WATCHED', label: 'Franchise Watched', field: null },
  { value: 'CUSTOM', label: 'Custom Field', field: null }
];

const ACHIEVEMENT_TEMPLATES = {
  SOCIAL_BUTTERFLY: {
    name: 'SOCIAL_BUTTERFLY',
    description: 'Gain 100 followers',
    category: 'SOCIAL',
    rarity: 'uncommon',
    points: 25,
    conditionType: 'FOLLOWER_COUNT',
    targetValue: 100,
    targetField: 'followersCount'
  },
  MOVIE_CRITIC: {
    name: 'MOVIE_CRITIC',
    description: 'Write 50 movie reviews',
    category: 'ENGAGEMENT',
    rarity: 'rare',
    points: 50,
    conditionType: 'REVIEW_COUNT',
    targetValue: 50,
    targetField: 'reviewsCount'
  },
  BINGE_WATCHER: {
    name: 'BINGE_WATCHER',
    description: 'Watch 100 movies',
    category: 'MILESTONE',
    rarity: 'epic',
    points: 100,
    conditionType: 'MOVIE_WATCHED_COUNT',
    targetValue: 100,
    targetField: 'moviesWatched'
  },
  ACTION_WATCH: {
    name: 'ACTION_WATCH_MOVIE',
    description: 'Earn points for watching movies',
    category: 'ACTION',
    rarity: 'common',
    points: 5,
    conditionType: 'ACTION_BASED',
    actionType: 'mark_watched',
    isRepeatable: true,
    maxProgress: 999
  },
  ACTION_REVIEW: {
    name: 'ACTION_SUBMIT_REVIEW',
    description: 'Earn points for submitting reviews',
    category: 'ACTION',
    rarity: 'common',
    points: 10,
    conditionType: 'ACTION_BASED',
    actionType: 'submit_review',
    isRepeatable: true,
    maxProgress: 999
  },
  SPECIFIC_MOVIE: {
    name: 'WATCH_SPECIFIC_MOVIE',
    description: 'Watch a specific movie',
    category: 'CONTENT',
    rarity: 'uncommon',
    points: 15,
    conditionType: 'SPECIFIC_MOVIE_WATCHED',
    contentIds: ['']
  },
  HORROR_FAN: {
    name: 'HORROR_ENTHUSIAST',
    description: 'Watch 20 horror movies',
    category: 'GENRE',
    rarity: 'rare',
    points: 40,
    conditionType: 'GENRE_WATCHED_COUNT',
    targetValue: 20,
    genreName: 'horror'
  }
};

const AchievementManager = () => {
  const { confirm } = useConfirm();
  const [achievements, setAchievements] = useState([]);
  const [filteredAchievements, setFilteredAchievements] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedAchievements, setSelectedAchievements] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [filterRarity, setFilterRarity] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'SOCIAL',
    rarity: 'common',
    points: 10,
    icon: 'EmojiEvents',
    conditionType: 'FOLLOWER_COUNT',
    actionType: '',
    targetValue: 1,
    targetField: 'followersCount',
    contentIds: [],
    genreName: '',
    franchiseId: '',
    isActive: true,
    isRepeatable: false,
    maxProgress: 1,
    threshold: 1
  });
  const [selectedTemplate, setSelectedTemplate] = useState('');

  useEffect(() => {
    loadAchievements();
  }, []);

  useEffect(() => {
    filterAndSortAchievements();
  }, [achievements, searchQuery, filterCategory, filterRarity, filterStatus, sortBy, sortOrder]);

  const loadAchievements = async () => {
    setLoading(true);
    try {
      const data = await adminAchievementService.getAllAchievements();
      setAchievements(data);
    } catch (error) {
      console.error('Error loading achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortAchievements = () => {
    let filtered = [...achievements];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(achievement => 
        achievement.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        achievement.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Category filter
    if (filterCategory !== 'ALL') {
      filtered = filtered.filter(achievement => achievement.category === filterCategory);
    }

    // Rarity filter
    if (filterRarity !== 'ALL') {
      filtered = filtered.filter(achievement => achievement.rarity === filterRarity);
    }

    // Status filter
    if (filterStatus !== 'ALL') {
      const isActive = filterStatus === 'ACTIVE';
      filtered = filtered.filter(achievement => achievement.isActive === isActive);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredAchievements(filtered);
  };

  const groupedAchievements = () => {
    const groups = {};
    filteredAchievements.forEach(achievement => {
      const category = achievement.category || 'UNCATEGORIZED';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(achievement);
    });
    return groups;
  };

  const handleBulkAction = async (action) => {
    if (selectedAchievements.length === 0) return;
    
    setLoading(true);
    try {
      switch (action) {
        case 'activate':
          await Promise.all(selectedAchievements.map(id => 
            adminAchievementService.updateAchievement(id, { isActive: true })
          ));
          break;
        case 'deactivate':
          await Promise.all(selectedAchievements.map(id => 
            adminAchievementService.updateAchievement(id, { isActive: false })
          ));
          break;
        case 'delete':
          const confirmed = await confirm({
            title: 'Delete Achievements',
            message: `Are you sure you want to delete ${selectedAchievements.length} achievements?`,
            confirmText: 'Delete',
            confirmColor: 'error'
          });
          if (confirmed) {
            await Promise.all(selectedAchievements.map(id => 
              adminAchievementService.deleteAchievement(id)
            ));
          }
          break;
      }
      setSelectedAchievements([]);
      await loadAchievements();
    } catch (error) {
      console.error('Bulk action error:', error);
    } finally {
      setLoading(false);
      setBulkMenuAnchor(null);
    }
  };

  const handleSelectAll = () => {
    if (selectedAchievements.length === filteredAchievements.length) {
      setSelectedAchievements([]);
    } else {
      setSelectedAchievements(filteredAchievements.map(a => a.id));
    }
  };

  const handleSelectAchievement = (id) => {
    setSelectedAchievements(prev => 
      prev.includes(id) 
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleSave = async () => {
    try {
      // Ensure contentIds is an array for save
      let contentIds = formData.contentIds;
      if (typeof contentIds === 'string') {
        contentIds = contentIds.split(',').map(id => id.trim()).filter(Boolean);
      }
      
      // Prepare data for save
      const saveData = {
        ...formData,
        contentIds: contentIds,
        actionType: formData.conditionType === 'ACTION_BASED' ? formData.actionType : undefined,
        conditions: JSON.stringify([{
          type: formData.conditionType,
          actionType: formData.conditionType === 'ACTION_BASED' ? formData.actionType : undefined,
          targetValue: formData.targetValue,
          targetField: formData.targetField,
          contentIds: contentIds,
          genreName: formData.genreName,
          franchiseId: formData.franchiseId
        }])
      };

      // Remove undefined fields
      Object.keys(saveData).forEach(key => 
        saveData[key] === undefined && delete saveData[key]
      );

      if (editingAchievement) {
        await adminAchievementService.updateAchievement(editingAchievement.id, saveData);
      } else {
        await adminAchievementService.createAchievement(saveData);
      }
      await loadAchievements();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving achievement:', error);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Achievement',
      message: 'Are you sure you want to delete this achievement?',
      confirmText: 'Delete',
      confirmColor: 'error',
      variant: 'error'
    });
    
    if (confirmed) {
      try {
        await adminAchievementService.deleteAchievement(id);
        await loadAchievements();
      } catch (error) {
        console.error('Error deleting achievement:', error);
      }
    }
  };

  const handleEdit = (achievement) => {
    setEditingAchievement(achievement);
    
    // Parse conditions if they exist
    let parsedConditions = {};
    if (achievement.conditions) {
      try {
        const conditions = JSON.parse(achievement.conditions);
        parsedConditions = conditions[0] || {};
      } catch (e) {
        console.warn('Could not parse achievement conditions:', e);
      }
    }
    
    setFormData({
      ...achievement,
      actionType: achievement.actionType || parsedConditions.actionType || '',
      contentIds: achievement.contentIds || parsedConditions.contentIds || [],
      genreName: achievement.genreName || parsedConditions.genreName || '',
      franchiseId: achievement.franchiseId || parsedConditions.franchiseId || '',
      targetValue: achievement.targetValue || parsedConditions.targetValue || 1,
      targetField: achievement.targetField || parsedConditions.targetField || 'followersCount'
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAchievement(null);
    setSelectedTemplate('');
    setFormData({
      name: '',
      description: '',
      category: 'SOCIAL',
      rarity: 'common',
      points: 10,
      icon: 'EmojiEvents',
      conditionType: 'FOLLOWER_COUNT',
      actionType: '',
      targetValue: 1,
      targetField: 'followersCount',
      contentIds: [],
      genreName: '',
      franchiseId: '',
      isActive: true,
      isRepeatable: false,
      maxProgress: 1,
      threshold: 1
    });
  };

  const applyTemplate = (templateKey) => {
    const template = ACHIEVEMENT_TEMPLATES[templateKey];
    if (template) {
      setFormData(prev => ({
        ...prev,
        ...template
      }));
    }
  };

  const handleConditionTypeChange = (conditionType) => {
    const conditionConfig = CONDITION_TYPES.find(c => c.value === conditionType);
    const updates = {
      conditionType,
      targetField: conditionConfig?.field || '',
      contentIds: [],
      genreName: '',
      franchiseId: '',
      actionType: ''
    };
    
    // Set defaults for ACTION_BASED
    if (conditionType === 'ACTION_BASED') {
      updates.isRepeatable = true;
      updates.maxProgress = 999;
    }
    
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const renderConditionFields = () => {
    switch (formData.conditionType) {
      case 'ACTION_BASED':
        return (
          <>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Action Type</InputLabel>
                <Select
                  value={formData.actionType}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    actionType: e.target.value
                  }))}
                >
                  <MenuItem value="mark_watched">Mark Watched</MenuItem>
                  <MenuItem value="submit_review">Submit Review</MenuItem>
                  <MenuItem value="add_to_watchlist">Add to Watchlist</MenuItem>
                  <MenuItem value="share">Share Content</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Action-Based Achievements</strong> award points every time the user performs the specified action.
                  These are repeatable and perfect for engagement rewards.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Example:</strong> Set points below to define how many points users earn per action.
                </Typography>
              </Alert>
            </Grid>
          </>
        );
      
      case 'SPECIFIC_MOVIE_WATCHED':
        return (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Movie ID"
              placeholder="Enter DynamoDB Movie ID"
              value={formData.contentIds[0] || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                contentIds: [e.target.value]
              }))}
              helperText="Enter the DynamoDB Movie table ID"
            />
          </Grid>
        );
      
      case 'SPECIFIC_SHOW_WATCHED':
        return (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Show ID"
              placeholder="Enter DynamoDB Show ID"
              value={formData.contentIds[0] || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                contentIds: [e.target.value]
              }))}
              helperText="Enter the DynamoDB Show table ID"
            />
          </Grid>
        );
      
      case 'MOVIE_GROUP_WATCHED':
        return (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="DynamoDB Movie IDs"
              placeholder="movie-id-1,movie-id-2,movie-id-3"
              value={Array.isArray(formData.contentIds) ? formData.contentIds.join(',') : formData.contentIds || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                contentIds: e.target.value
              }))}
              onBlur={(e) => {
                // Parse comma-separated values on blur
                const ids = e.target.value.split(',').map(id => id.trim()).filter(Boolean);
                setFormData(prev => ({ ...prev, contentIds: ids }));
              }}
              helperText="Enter comma-separated DynamoDB Movie table IDs (e.g., movie-123,movie-456)"
              multiline
              rows={3}
            />
          </Grid>
        );
      
      case 'GENRE_WATCHED_COUNT':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Genre Name"
                placeholder="e.g., horror, action, comedy"
                value={formData.genreName}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  genreName: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Target Count"
                type="number"
                value={formData.targetValue}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  targetValue: parseInt(e.target.value)
                }))}
              />
            </Grid>
          </>
        );
      
      case 'FRANCHISE_WATCHED':
        return (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Franchise ID"
              placeholder="Enter franchise identifier"
              value={formData.franchiseId}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                franchiseId: e.target.value
              }))}
            />
          </Grid>
        );
      
      case 'CUSTOM':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Target Field"
                placeholder="e.g., customField"
                value={formData.targetField}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  targetField: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Target Value"
                type="number"
                value={formData.targetValue}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  targetValue: parseInt(e.target.value)
                }))}
              />
            </Grid>
          </>
        );
      
      default:
        return (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Target Value"
              type="number"
              value={formData.targetValue}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                targetValue: parseInt(e.target.value)
              }))}
            />
          </Grid>
        );
    }
  };

  const getRarityColor = (rarity) => {
    return RARITY_OPTIONS.find(r => r.value === rarity)?.color || '#9E9E9E';
  };

  const tabCategories = ['ALL', ...CATEGORY_OPTIONS];
  const categoryGroups = groupedAchievements();

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Achievement Management</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadAchievements}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
          >
            Create Achievement
          </Button>
        </Box>
      </Box>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <MenuItem value="ALL">All Categories</MenuItem>
                  {CATEGORY_OPTIONS.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Rarity</InputLabel>
                <Select
                  value={filterRarity}
                  onChange={(e) => setFilterRarity(e.target.value)}
                >
                  <MenuItem value="ALL">All Rarities</MenuItem>
                  {RARITY_OPTIONS.map(rarity => (
                    <MenuItem key={rarity.value} value={rarity.value}>{rarity.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="ALL">All Status</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="category">Category</MenuItem>
                  <MenuItem value="rarity">Rarity</MenuItem>
                  <MenuItem value="points">Points</MenuItem>
                  <MenuItem value="createdAt">Created</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <IconButton
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
              >
                <Sort sx={{ transform: sortOrder === 'desc' ? 'rotate(180deg)' : 'none' }} />
              </IconButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedAchievements.length > 0 && (
        <Card sx={{ mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <CardContent sx={{ py: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">
                {selectedAchievements.length} achievement(s) selected
              </Typography>
              <Box>
                <Button
                  size="small"
                  onClick={() => setBulkMenuAnchor(document.getElementById('bulk-menu-button'))}
                  id="bulk-menu-button"
                  sx={{ color: 'inherit' }}
                >
                  Bulk Actions
                </Button>
                <Menu
                  anchorEl={bulkMenuAnchor}
                  open={Boolean(bulkMenuAnchor)}
                  onClose={() => setBulkMenuAnchor(null)}
                >
                  <MenuItem onClick={() => handleBulkAction('activate')}>
                    <ListItemIcon><Visibility /></ListItemIcon>
                    <ListItemText>Activate</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleBulkAction('deactivate')}>
                    <ListItemIcon><VisibilityOff /></ListItemIcon>
                    <ListItemText>Deactivate</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => handleBulkAction('delete')} sx={{ color: 'error.main' }}>
                    <ListItemIcon><Delete color="error" /></ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                  </MenuItem>
                </Menu>
                <Button
                  size="small"
                  onClick={() => setSelectedAchievements([])}
                  sx={{ color: 'inherit', ml: 1 }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Grouped View */}
      <Box>
        {Object.entries(categoryGroups).map(([category, categoryAchievements]) => (
          <Accordion key={category} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h6">{category}</Typography>
                <Badge badgeContent={categoryAchievements.length} color="primary" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedAchievements.length > 0 && selectedAchievements.length < categoryAchievements.length}
                          checked={categoryAchievements.length > 0 && categoryAchievements.every(a => selectedAchievements.includes(a.id))}
                          onChange={() => {
                            const categoryIds = categoryAchievements.map(a => a.id);
                            const allSelected = categoryIds.every(id => selectedAchievements.includes(id));
                            if (allSelected) {
                              setSelectedAchievements(prev => prev.filter(id => !categoryIds.includes(id)));
                            } else {
                              setSelectedAchievements(prev => [...new Set([...prev, ...categoryIds])]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Condition Type</TableCell>
                      <TableCell>Rarity</TableCell>
                      <TableCell>Points</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categoryAchievements.map((achievement) => (
                      <TableRow key={achievement.id} selected={selectedAchievements.includes(achievement.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedAchievements.includes(achievement.id)}
                            onChange={() => handleSelectAchievement(achievement.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <EmojiEvents />
                            <Box>
                              <Typography variant="subtitle2">{achievement.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {achievement.description}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            <Chip 
                              label={achievement.conditionType || 'Legacy'} 
                              size="small" 
                              variant="outlined"
                            />
                            {achievement.conditionType === 'ACTION_BASED' && achievement.actionType && (
                              <Chip
                                label={achievement.actionType}
                                size="small"
                                color="primary"
                                variant="filled"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={achievement.rarity}
                            size="small"
                            sx={{
                              bgcolor: getRarityColor(achievement.rarity),
                              color: 'white'
                            }}
                          />
                        </TableCell>
                        <TableCell>{achievement.points}</TableCell>
                        <TableCell>
                          <Chip
                            label={achievement.isActive ? 'Active' : 'Inactive'}
                            color={achievement.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => handleEdit(achievement)} size="small">
                            <Edit />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(achievement.id)} size="small">
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setDialogOpen(true)}
      >
        <Add />
      </Fab>



      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAchievement ? 'Edit Achievement' : 'Create Achievement'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Template</InputLabel>
                <Select
                  value={selectedTemplate}
                  onChange={(e) => {
                    setSelectedTemplate(e.target.value);
                    if (e.target.value) {
                      applyTemplate(e.target.value);
                    }
                  }}
                >
                  <MenuItem value="">Custom Achievement</MenuItem>
                  <MenuItem value="SOCIAL_BUTTERFLY">👥 Social Butterfly (100 Followers)</MenuItem>
                  <MenuItem value="MOVIE_CRITIC">📝 Movie Critic (50 Reviews)</MenuItem>
                  <MenuItem value="BINGE_WATCHER">🎬 Binge Watcher (100 Movies)</MenuItem>
                  <MenuItem value="ACTION_WATCH">⚡ Action: Watch Movie (Repeatable)</MenuItem>
                  <MenuItem value="ACTION_REVIEW">⚡ Action: Submit Review (Repeatable)</MenuItem>
                  <MenuItem value="SPECIFIC_MOVIE">🎭 Specific Movie Achievement</MenuItem>
                  <MenuItem value="HORROR_FAN">👻 Horror Enthusiast (20 Horror Movies)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                >
                  {CATEGORY_OPTIONS.map(category => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Rarity</InputLabel>
                <Select
                  value={formData.rarity}
                  onChange={(e) => setFormData(prev => ({ ...prev, rarity: e.target.value }))}
                >
                  {RARITY_OPTIONS.map(rarity => (
                    <MenuItem key={rarity.value} value={rarity.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          width={12}
                          height={12}
                          borderRadius="50%"
                          bgcolor={rarity.color}
                        />
                        {rarity.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Points"
                type="number"
                value={formData.points}
                onChange={(e) => setFormData(prev => ({ ...prev, points: parseInt(e.target.value) }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Threshold"
                type="number"
                value={formData.threshold}
                onChange={(e) => setFormData(prev => ({ ...prev, threshold: parseInt(e.target.value) }))}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Condition Type</InputLabel>
                <Select
                  value={formData.conditionType}
                  onChange={(e) => handleConditionTypeChange(e.target.value)}
                >
                  {CONDITION_TYPES.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {renderConditionFields()}

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isRepeatable}
                    onChange={(e) => setFormData(prev => ({ ...prev, isRepeatable: e.target.checked }))}
                  />
                }
                label="Repeatable"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" startIcon={<Save />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AchievementManager;